import { useCallback } from "react";
import ValetApi from "../api/Api";
import { toast } from "react-toastify";

/**
 * Garage search handlers
 * - No useEffect here
 * - Only updates state when handlers are explicitly called
 */
const useGarageSearchHandlers = (userDetail, setGarageData, setError) => {
    const updateError = useCallback(
        (message = null) => {
            if (typeof setError === "function") setError(message);
        },
        [setError],
    );

    const updateGarageData = useCallback(
        (rows = []) => {
            if (typeof setGarageData === "function") {
                setGarageData(Array.isArray(rows) ? rows : []);
            }
        },
        [setGarageData],
    );

    const normalizeError = (err, fallbackMessage) => err?.response?.data?.error || err?.message || fallbackMessage;

    const handleSearchByMobile = useCallback(
        async (podiumId, mobile) => {
            try {
                updateError(null);

                const parsedPodiumId = Number(podiumId) || null;
                const mobileValue = String(mobile || "").trim();

                if (!parsedPodiumId) {
                    updateGarageData([]);
                    return { success: false, message: "Active podium is missing.", data: [] };
                }

                if (!mobileValue) {
                    updateGarageData([]);
                    return { success: false, message: "Mobile is required.", data: [] };
                }

                const res = await ValetApi.getByPodiumMobile(parsedPodiumId, mobileValue);
                const rows = Array.isArray(res?.data?.garageData) ? res.data.garageData : [];

                if (rows.length === 0) {
                    updateGarageData([]);
                    return { success: false, message: "No vehicles found for that mobile number.", data: [] };
                }

                updateGarageData(rows);
                return { success: true, message: null, data: rows };
            } catch (err) {
                const message = err?.response?.data?.error || err?.response?.data?.message || err?.message || "Search failed";

                updateGarageData([]);
                updateError(message);
                return { success: false, message, data: [] };
            }
        },
        [updateError, updateGarageData],
    );

    const handleReset = useCallback(
        async (podiumId) => {
            try {
                updateError(null);

                const parsedPodiumId = Number(podiumId);
                if (!parsedPodiumId) {
                    setGarageData([]);
                    return { success: false, data: [] };
                }

                const res = await ValetApi.getGarageDataActiveByPodiumId(parsedPodiumId);
                const garageRows = res?.data?.garageData || [];

                setGarageData(garageRows);
                return { success: true, data: garageRows };
            } catch (err) {
                setGarageData([]);
                updateError(normalizeError(err, "Reset failed"));
                return { success: false, data: [] };
            }
        },
        [updateError, setGarageData],
    );

    const handleLostKeys = useCallback(
        async (userId, transactionId) => {
            try {
                updateError(null);

                const parsedUserId = Number(userId || userDetail?.id);
                const parsedTransactionId = Number(transactionId);

                if (!parsedUserId || !parsedTransactionId) {
                    return { success: false };
                }

                await ValetApi.handleLostKeys(parsedUserId, parsedTransactionId);
                toast.success("Vehicle moved to Lost Keys.", { autoClose: 1000 });
                return { success: true };
            } catch (err) {
                const message = normalizeError(err, "Lost keys failed");
                updateError(message);
                toast.error(message, { autoClose: 1000 });
                return { success: false };
            }
        },
        [updateError, userDetail],
    );

    return {
        handleSearchByMobile,
        handleReset,
        handleLostKeys,
    };
};

export default useGarageSearchHandlers;
